import {
  Bin,
  Checkbox,
  Modal,
  PrimaryButton,
  SearchField,
  SecondaryButton,
  SnackbarStatus,
  Spinner,
  StatusBar,
  Typography,
  VenueIcon,
  WarningIcon,
} from '@weareopenr/component-library';
import { ChangeEvent, Fragment, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import chefPlating from 'src/assets/images/chef-plating.png';
import { commitPublishPlan } from 'src/api/commitPublishPlan/commitPublishPlan';
import { rejectPublishPlan } from 'src/api/rejectPublishPlan/rejectPublishPlan';
import { ApiError } from 'src/helpers/apiError';
import { getBrandId } from 'src/helpers/getBrandId/getBrandId';
import { capitalise } from 'src/helpers/capitalise';
import { pluralize } from 'src/helpers/pluralize/pluralize';
import { isPublishReviewFor } from 'src/helpers/publishReviewNotification/publishReviewNotification';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';
import { useUpdateNotification } from 'src/hooks/useUpdateNotification/useUpdateNotification';
import type {
  ConnectorPlan,
  ConnectorPlanBlocker,
  ConnectorPlanChange,
  ConnectorPlanDiff,
  ConnectorPlanEntityType,
  ConnectorPlanRow,
  ConnectorPlanTarget,
} from '@weareopenr/types';
import { ButtonContainer } from '../ButtonContainer/ButtonContainer';
import styles from './PublishReviewModal.module.scss';
import type { IReviewDecision } from 'src/hooks/usePublishPlans/usePublishPlans';
import { ReviewDecisionAlert, ReviewExpiredAlert } from './ReviewAlert';

type TProductStep = keyof ConnectorPlanDiff;
type TReviewStep = 'blockers' | TProductStep;
type TPlanNoun = 'product' | 'price';

const ROW_STEPS: TReviewStep[] = ['inserts', 'updates', 'deletes'];

const PRODUCT_STEP_WORDS: Record<TProductStep, string> = {
  inserts: 'new',
  updates: 'updated',
  deletes: 'removed',
};

// A connector may send types of its own, so anything missing here shows the raw value.
const ENTITY_TYPE_LABELS: Partial<Record<ConnectorPlanEntityType, string>> = {
  ITEM: 'Item',
  BUNDLE: 'Bundle',
  OPTION_GROUP: 'Option group',
  OPTION: 'Option',
  PRICE: 'Price',
  OPTION_PRICE: 'Option price',
  VENUE: 'Venue',
  ITEM_AVAILABILITY: 'Item availability',
  DAYPART: 'Day part',
  COMBO_MEAL: 'Combo meal',
  COMBO_SIDE_GROUP: 'Combo side group',
  COMBO_SIDE_PRICE: 'Combo side price',
  CONDIMENT_GROUP: 'Condiment group',
  CONDIMENT_SET: 'Default build',
  CLASS: 'Class',
};

// Oracle classes are matched by their own number, not a PLU
const TYPES_WITHOUT_PLU = new Set<ConnectorPlanEntityType>(['CLASS']);

const ERROR_MESSAGES = {
  load: "Couldn't load the changes. Publish again to get a new preview.",
  accept: 'An error has occurred; the changes could not be accepted.',
  reject: 'An error has occurred; the changes could not be rejected.',
};

export const getEntityTypeLabel = (type: ConnectorPlanEntityType): string => ENTITY_TYPE_LABELS[type] ?? type;

const formatFieldName = (field: string): string => capitalise(field.replace(/_/g, ' '));

const getStepItems = (plan: ConnectorPlan, step: TReviewStep): (ConnectorPlanRow | ConnectorPlanBlocker)[] =>
  step === 'blockers' ? plan.blockers : plan.diff[step];

// A publish can hold a products plan and a prices plan together, and the products wording covers both
const getPlanNoun = (plans: ConnectorPlan[]): TPlanNoun =>
  plans.every(({ kind }) => kind === 'prices') ? 'price' : 'product';

const getStepTitle = (step: TReviewStep, noun: TPlanNoun): string =>
  step === 'blockers' ? 'Blockers' : `${capitalise(PRODUCT_STEP_WORDS[step])} ${noun}s`;

export const getSystemNames = (plans: ConnectorPlan[]): string =>
  [...new Set(plans.map(({ target }) => target.system_name))].join(' and ');

const getStepSubtitle = (step: TReviewStep, plans: ConnectorPlan[], noun: TPlanNoun): string => {
  // Blockers can need fixing in openr or the external system, so each blocker's hint says where
  if (step === 'blockers') {
    return 'These must be fixed before anything can be published. You can still review the rest of the publish.';
  }
  return `Review ${noun} changes before either accepting or rejecting the publish. No changes will be made in ${getSystemNames(plans)} until you have accepted the publish.`;
};

const getAcknowledgeLabel = (step: TReviewStep, count: number, noun: TPlanNoun): string => {
  if (step === 'blockers') {
    return `I acknowledge that I have reviewed ${count} ${pluralize('blocker', count)}`;
  }
  return `I acknowledge that I am accepting ${count} ${PRODUCT_STEP_WORDS[step]} ${pluralize(noun, count)}`;
};

const getDeleteWarning = (count: number, plans: ConnectorPlan[]): string =>
  `${count} ${pluralize('item', count)} will be deleted from ${getSystemNames(plans)}. ${count === 1 ? 'It stops' : 'They stop'} selling as soon as the publish completes.`;

const getExternalIdLabel = (type: ConnectorPlanEntityType, externalId: string | null): string | null =>
  externalId && !TYPES_WITHOUT_PLU.has(type) ? `PLU ${externalId}` : externalId;

const joinDetails = (details: (string | null | undefined)[]): string => details.filter(Boolean).join(' · ');

const getBlockerSubtitle = ({ type, external_id, fix_hint }: ConnectorPlanBlocker): string =>
  joinDetails([getEntityTypeLabel(type), getExternalIdLabel(type, external_id), fix_hint]);

interface IProductRow {
  key: string;
  row: ConnectorPlanRow;
  venue?: ConnectorPlanTarget['venue'];
}

interface IVenueGroup {
  key: string;
  venueName?: string;
  products: IProductRow[];
}

const getProductRows = (plans: ConnectorPlan[], step: TProductStep): IProductRow[] =>
  plans.flatMap(({ event_id: eventId, target, diff }) =>
    diff[step].map((row) => ({ key: `${eventId}-${row.id}`, row, venue: target.venue })),
  );

// Venue-scoped prices plans repeat the same rows per venue, so each venue gets its own heading. Rows from
// plans without a venue stay ungrouped.
const groupByVenue = (products: IProductRow[]): IVenueGroup[] =>
  products.reduce<IVenueGroup[]>((groups, product) => {
    const key = product.venue?.venue_id ?? 'no-venue';
    const group = groups.find((existing) => existing.key === key);
    if (group) {
      group.products.push(product);
    } else {
      groups.push({ key, venueName: product.venue?.name, products: [product] });
    }
    return groups;
  }, []);

const getProductSubtitle = ({ row }: IProductRow): string =>
  joinDetails([row.subtitle, getExternalIdLabel(row.type, row.external_id)]);

const matchesSearch = ({ row, venue }: IProductRow, searchTerm: string): boolean =>
  [row.name, row.subtitle, row.external_id, venue?.name].some((value) =>
    value?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

const isFlagged = ({ row }: IProductRow): boolean => row.flags.length > 0;

const getErrorMessage = (error: unknown, fallback: string): string =>
  error instanceof ApiError ? error.message : fallback;

const BlockerCard = ({ blocker }: { blocker: ConnectorPlanBlocker }) => (
  <div className={styles['blocker-card']}>
    <span className={styles['blocker-dot']} />
    <div className={styles['blocker-text']}>
      <Typography size="medium" weight="semi-bold">
        {blocker.message}
      </Typography>
      <Typography size="small" className={styles['grey-text']}>
        {getBlockerSubtitle(blocker)}
      </Typography>
    </div>
  </div>
);

// Blockers stop the whole publish, so they're listed together rather than under each plan
const BlockerList = ({ blockers }: { blockers: ConnectorPlanBlocker[] }) => (
  <div className={styles['plan-section']}>
    <Typography size="medium" weight="bold" className={styles['list-heading']}>
      {pluralize('Blocker', blockers.length)}
    </Typography>
    {blockers.map((blocker) => (
      <BlockerCard key={`${blocker.type}-${blocker.external_id ?? blocker.name}-${blocker.code}`} blocker={blocker} />
    ))}
  </div>
);

// A create only has a new value and a removal only an old one, so either side can be missing
const ChangeRow = ({ field, old_value, new_value }: ConnectorPlanChange) => (
  <div className={styles['change']}>
    <Typography size="medium" className={styles['change-field']}>
      {formatFieldName(field)}
    </Typography>
    {old_value !== null && (
      <del className={styles['old-value']}>
        <Typography size="medium" weight="semi-bold" inline>
          {old_value}
        </Typography>
      </del>
    )}
    {old_value !== null && new_value !== null && (
      <Typography size="medium" inline className={styles['grey-text']}>
        →
      </Typography>
    )}
    {new_value !== null && (
      <ins className={styles['new-value']}>
        <Typography size="medium" weight="semi-bold" inline>
          {new_value}
        </Typography>
      </ins>
    )}
  </div>
);

const ProductRow = ({ product, step }: { product: IProductRow; step: TProductStep }) => {
  const { row } = product;
  const subtitle = getProductSubtitle(product);
  const isDelete = step === 'deletes';

  return (
    <div className={styles['product-row']}>
      <div className={styles['product-details']}>
        <div className={styles['product-name']}>
          {isDelete && <Bin size="small" className={styles['delete-icon']} />}
          <Typography size="medium" weight="semi-bold">
            {row.name}
          </Typography>
        </div>
        <div className={styles['product-subtitle']}>
          {subtitle && (
            <Typography size="small" className={styles['grey-text']}>
              {subtitle}
            </Typography>
          )}
          {row.type !== 'ITEM' && (
            <Typography size="small" inline className={styles['type-badge']}>
              {getEntityTypeLabel(row.type)}
            </Typography>
          )}
        </div>
        {row.flags.map(({ code, severity, message }) => (
          <div key={code} className={classNames(styles['flag'], styles[`flag-${severity}`])}>
            {severity === 'warning' && <WarningIcon size="small" className={styles['flag-icon']} />}
            <Typography size="small">{message}</Typography>
          </div>
        ))}
      </div>
      {isDelete ? (
        <Typography size="medium" weight="semi-bold" className={styles['delete-text']}>
          Will be deleted
        </Typography>
      ) : (
        <div className={styles['changes']}>
          {row.changes.map((change, index) => (
            <ChangeRow key={`${change.field}-${index}`} {...change} />
          ))}
        </div>
      )}
    </div>
  );
};

const VenueHeading = ({ venueName, count }: { venueName: string; count: number }) => (
  <div className={styles['venue-heading']}>
    <VenueIcon className={styles['venue-icon']} />
    <Typography size="medium" weight="bold">
      {venueName}
    </Typography>
    <Typography size="medium" className={styles['grey-text']}>
      {`${count} ${pluralize('change', count)}`}
    </Typography>
  </div>
);

interface IProductListProps {
  plans: ConnectorPlan[];
  step: TProductStep;
  noun: TPlanNoun;
}

const ProductList = ({ plans, step, noun }: IProductListProps) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isFlaggedOnly, setIsFlaggedOnly] = useState<boolean>(false);

  const products = getProductRows(plans, step);
  const flaggedCount = products.filter(isFlagged).length;
  const visibleProducts = products.filter(
    (product) => matchesSearch(product, searchTerm) && (!isFlaggedOnly || isFlagged(product)),
  );

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => setSearchTerm(event.target.value);
  const toggleFlaggedOnly = () => setIsFlaggedOnly((prev) => !prev);

  return (
    <div className={styles['product-list']}>
      <div className={styles['list-header']}>
        <div className={styles['list-controls']}>
          <SearchField isDisabled={false} placeholder="Search items" onChange={handleSearch} />
          <SecondaryButton
            buttonStyle="outline"
            size="small"
            icon={WarningIcon}
            aria-pressed={isFlaggedOnly}
            className={classNames({ [styles['filter-active']]: isFlaggedOnly })}
            onClick={toggleFlaggedOnly}
          >
            {`${isFlaggedOnly ? 'Flagged only' : 'Flagged'} · ${flaggedCount}`}
          </SecondaryButton>
        </div>
      </div>
      {step === 'deletes' && noun === 'product' && products.length > 0 && (
        <div className={styles['delete-banner']}>
          <Bin size="small" className={styles['delete-icon']} />
          <Typography size="medium">{getDeleteWarning(products.length, plans)}</Typography>
        </div>
      )}
      <div className={styles['product-rows']}>
        {visibleProducts.length === 0 ? (
          <Typography size="medium" className={styles['empty-message']}>
            {products.length === 0 ? `No ${PRODUCT_STEP_WORDS[step]} ${noun}s` : `No ${noun}s match your search.`}
          </Typography>
        ) : (
          groupByVenue(visibleProducts).map(({ key, venueName, products: venueProducts }) => (
            <Fragment key={key}>
              {venueName && <VenueHeading venueName={venueName} count={venueProducts.length} />}
              {venueProducts.map((product) => (
                <ProductRow key={product.key} product={product} step={step} />
              ))}
            </Fragment>
          ))
        )}
      </div>
    </div>
  );
};

const StepContent = ({ step, plans, noun }: { step: TReviewStep; plans: ConnectorPlan[]; noun: TPlanNoun }) => {
  if (step === 'blockers') {
    return (
      <div className={styles['card-list']}>
        <BlockerList blockers={plans.flatMap(({ blockers }) => blockers)} />
      </div>
    );
  }
  // Keyed by step so the search and filter start empty on each step
  return <ProductList key={step} plans={plans} step={step} noun={noun} />;
};

interface IPublishReviewModalProps {
  isOpen: boolean;
  rootEventId: string;
  plans: ConnectorPlan[];
  isLoading: boolean;
  isError: boolean;
  isExpired: boolean;
  decision: IReviewDecision | null;
  closeModal: () => void;
  onPublishAgain: () => void;
}

export const PublishReviewModal = ({
  isOpen,
  rootEventId,
  plans,
  isLoading,
  isError,
  isExpired,
  decision,
  closeModal,
  onPublishAgain,
}: IPublishReviewModalProps) => {
  const queryClient = useQueryClient();
  const { errorMessage, showNotification, clearError } = useModalNotification();
  const { markNotificationsRead } = useUpdateNotification();
  const [stepCount, setStepCount] = useState<number>(0);
  const [isChangeAccepted, setIsChangeAccepted] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const blockerCount = plans.reduce((total, { blockers }) => total + blockers.length, 0);
  const hasBlockers = blockerCount > 0;
  const steps: TReviewStep[] = hasBlockers ? ['blockers', ...ROW_STEPS] : ROW_STEPS;
  // A plan dropping out can shrink the steps for a render before the reset effect below runs
  const stepIndex = stepCount < steps.length ? stepCount : 0;
  const step = steps[stepIndex];
  const isLastStep = stepIndex === steps.length - 1;
  const noun = getPlanNoun(plans);
  const stepItemCount = plans.reduce((total, plan) => total + getStepItems(plan, step).length, 0);
  const isDecided = decision !== null;
  const canAccept = !hasBlockers && !isDecided && !isExpired && plans.every(({ can_commit }) => can_commit);
  // A decided plan stays browsable after it expires, so its decision on the last step can still be seen
  const canMoveOn = isLastStep ? canAccept : !isExpired || isDecided;
  const reviewedEventIds = plans.map(({ event_id }) => event_id);
  const planEventIds = reviewedEventIds.join();
  const eventQueryKey = [QueryKeys.events, getBrandId(), rootEventId];

  useEffect(() => {
    setIsChangeAccepted(false);
  }, [stepCount]);

  // Connectors send their plans at different times, so a late one restarts the review rather than
  // being accepted unseen
  useEffect(() => {
    setStepCount(0);
    setIsChangeAccepted(false);
  }, [planEventIds]);

  useEffect(() => {
    if (!isOpen) {
      // Cleared here too, as closing on the first step leaves the step unchanged
      setStepCount(0);
      setIsChangeAccepted(false);
      clearError();
    }
  }, [isOpen, clearError]);

  const stepBack = () => setStepCount(stepIndex - 1);
  const stepNext = () => setStepCount(stepIndex + 1);

  const decide = async (action: (eventId: string) => Promise<void>, fallbackMessage: string) => {
    clearError();
    setIsSubmitting(true);
    try {
      await action(rootEventId);
      // One decision covers every channel under the publish, so each of their reviews is done
      markNotificationsRead(isPublishReviewFor(rootEventId));
      // Closed first, so the refreshed event doesn't flash this user's own decision as someone else's
      closeModal();
      await queryClient.invalidateQueries({ queryKey: eventQueryKey });
    } catch (error) {
      showNotification({ status: SnackbarStatus.ERROR, message: getErrorMessage(error, fallbackMessage) });
      // A refusal can mean someone else decided first, which the refreshed event shows
      await queryClient.invalidateQueries({ queryKey: eventQueryKey });
    } finally {
      setIsSubmitting(false);
    }
  };

  const accept = () => decide((eventId) => commitPublishPlan(eventId, reviewedEventIds), ERROR_MESSAGES.accept);
  const reject = () => decide(rejectPublishPlan, ERROR_MESSAGES.reject);

  let content: JSX.Element;
  if (isLoading) {
    content = (
      <div className={styles['spinner']}>
        <Spinner size="large" data-testid="spinner" />
      </div>
    );
  } else if (isError || plans.length === 0) {
    content = <StatusBar message={ERROR_MESSAGES.load} variant="error" />;
  } else {
    content = (
      <>
        <Typography size="medium" weight="regular" className={styles['review-steps']}>
          {`Step ${stepIndex + 1} of ${steps.length}`}
        </Typography>
        <Typography size="xxlarge" weight="semi-bold">
          {getStepTitle(step, noun)}
        </Typography>
        <Typography className={styles['subtitle']}>{getStepSubtitle(step, plans, noun)}</Typography>
        {isExpired && !isDecided && (
          <div className={styles['review-alert']}>
            <ReviewExpiredAlert systemNames={getSystemNames(plans)} onPublishAgain={onPublishAgain} />
          </div>
        )}

        <StepContent step={step} plans={plans} noun={noun} />

        <Checkbox
          label={getAcknowledgeLabel(step, stepItemCount, noun)}
          name="acknowledge-step"
          labelWeight="regular"
          onChange={() => setIsChangeAccepted((prev) => !prev)}
          checked={isChangeAccepted}
        />
        {isLastStep && hasBlockers && (
          <Typography size="medium" className={styles['blocked-message']}>
            {`Fix ${blockerCount} ${pluralize('blocker', blockerCount)} before publishing`}
          </Typography>
        )}
        {isLastStep && decision && (
          <div className={styles['decision-alert']}>
            <ReviewDecisionAlert
              decision={decision}
              systemNames={getSystemNames(plans.filter(({ event_id }) => decision.eventIds.includes(event_id)))}
              onClose={closeModal}
              onPublishAgain={onPublishAgain}
            />
          </div>
        )}

        <ButtonContainer
          left={
            <SecondaryButton
              data-testid="reject-button"
              buttonStyle="outline"
              onClick={reject}
              size="full-width"
              disabled={isSubmitting || isExpired || isDecided}
            >
              Reject publish
            </SecondaryButton>
          }
          right={
            <PrimaryButton
              onClick={isLastStep ? accept : stepNext}
              size="full-width"
              disabled={!isChangeAccepted || isSubmitting || !canMoveOn}
              data-testid="next-accept-button"
              loading={isSubmitting}
            >
              {isLastStep ? 'Accept publish' : 'Next'}
            </PrimaryButton>
          }
        />
        <div className={styles['error-banner']}>
          {errorMessage && !isDecided && <StatusBar message={errorMessage} variant="error" />}
        </div>
      </>
    );
  }

  return (
    <Modal
      open={isOpen}
      closeModal={closeModal}
      image={chefPlating}
      imageAlt="chef"
      contentWidth="wide"
      onBack={stepIndex > 0 ? stepBack : undefined}
    >
      {content}
    </Modal>
  );
};
